import { LitElement, html, css } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import '@maersk-global/mds-components-core/mc-modal'
import '@maersk-global/mds-components-core/mc-input'
import '@maersk-global/mds-components-core/mc-button'
import "@maersk-global/mds-components-core/mc-loading-indicator";
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';

import { VoyageImoModel } from '../../../src/models/voyage-imo-model'
import { VoyageImoEventNames } from '../../../src/models/voyage-imo-model-impl'
import { SchedulesService } from '../../../src/services/schedules-service'; //fetchVesselsList
import '../../voy-vessel-search/src/voy-vessel-search';
import { vesselInfo } from '../../../src/models/types/voyage-schedules-model.types'

type PopupTypes = 'missing' | 'unauthorized' | 'default'

type PopupContent = Record<PopupTypes, { title: string; description: string }>

const createPopupContent = (): PopupContent => ({
    missing: {
        title: 'MISSING IMO',
        description: 'Please provide a 7-digit IMO number to continue.',
    },
    unauthorized: {
        title: 'UNAUTHORIZED IMO',
        description: `You are not authorized to view IMO ${VoyageImoModel?.imo ?? 'N/A'}.`,
    },
    default: {
        title: 'ENTER Vessel or IMO',
        description: 'Please provide a 7-digit IMO number to continue.',
    },
})

@customElement('imo-input-popup')
export class ImoInputPopup extends LitElement {
    @state() private imoValue = ''
    @state() private isInvalid = false
    @state() private popupType: PopupTypes = 'default'
    @state() private vessels = [];
    @state() private vesselListFetched = false;
    @state() private isUnauthorizedVessel = false;
    @property({type: String, attribute:'app-name'}) appName: string = "Vessel Voyage";

    @property({ type: Boolean }) isOpen = false

    private popupContent = createPopupContent()

    constructor() {
        super();
    }

    static get styles() {
        return [
            css`
            :host {
                font-family: MaerskText;
            }
            .logo-wrapper {
                grid-column: 1;
                display: flex;
                align-items: center;
                background-color: white;
                padding: 0px 0px 15px;
                border-bottom: 1px solid #f5f5f5;
                justify-content: center;
            }

            .name {
                color: var(--primary-blue-900, white);
                font-family: var(--component-heading-ff, 'serif');
                font-size: 20px;
                line-height: 24px;
                font-weight: normal;
                margin: 0 0px 0 24px;
            }

            .logo-divider {
                border-left: 3px solid var(--primary-blue-300, blue);
                width: 1px;
                height: 15px;
                margin: 2px 4px 0px 6px;
            }

            .app-name {
                color: var(--primary-blue-900, blue);
                font-family: var(--component-heading-ff, 'serif');
                font-size: 20px;
                line-height: 24px;
                font-weight: bold;
            }

            .logo {
                display: flex;
                align-items: center;
                height: 49px;
            }  
            .version {
                text-transform: uppercase;
                margin-top: -10px;
                border-radius: 6px;
                line-height: 12px;
                padding: 2px 4px 2px 8px;
                font-size: 12px;
                font-family: MaerskText;
                color
            }
            .left {
                display: flex;
                align-items: center;
            } 
            .spinner {
                margin: 70px 0 0;
            } 
            .logo-icon {
                height: 25px;
                width: auto;
                margin-left: 5px;
                display: inline-flex;
            }
            `   
        ];
    }

    firstUpdated() {
        const isVesselUser = sessionStorage.getItem('isVesselUser');
        if(isVesselUser !== 'Yes') {
            this._getVesselList().then( (res: any) => {
                    this.vessels = res;
                    const currentImo = VoyageImoModel?.imo;
                    if (currentImo) {
                        const isAuthorized = res?.some((vessel: vesselInfo) => vessel.imo?.toString() === currentImo.toString());
                        this.isUnauthorizedVessel = !isAuthorized;
                    }
            }).catch( (err: any) => {
                console.log(err);
            });
        }
    }

    connectedCallback() {
        super.connectedCallback()
        VoyageImoModel?.addEventListener(
            VoyageImoEventNames.UnauthorizedImo,
            this.handleUnauthorizedImo,
        )
        VoyageImoModel?.addEventListener(
            VoyageImoEventNames.MissingImo,
            this.handleMissingImo,
        )
        VoyageImoModel?.addEventListener(
            VoyageImoEventNames.showImoPopup,
            this.openPopup,
        )
        VoyageImoModel?.addEventListener(
            VoyageImoEventNames.ImoUpdated, 
            this.closePopup,
        );
    }

    disconnectedCallback() {
        super.disconnectedCallback()
        VoyageImoModel?.removeEventListener(
            VoyageImoEventNames.UnauthorizedImo,
            this.handleUnauthorizedImo,
        )
        VoyageImoModel?.removeEventListener(
            VoyageImoEventNames.MissingImo,
            this.handleMissingImo,
        )
        VoyageImoModel?.removeEventListener(
            VoyageImoEventNames.showImoPopup,
            this.openPopup,
        )
        VoyageImoModel?.removeEventListener(
            VoyageImoEventNames.ImoUpdated, 
            this.closePopup,
        );
    }

    private openPopup = () => {
        this.isOpen = true
        this.requestUpdate()
    }

    private closePopup = () => {
        this.isOpen = false
        this.requestUpdate()
    }

    private handleUnauthorizedImo = () => {
        this.updatePopupType('unauthorized')
    }

    private handleMissingImo = () => {
        this.updatePopupType('missing')
    }

    private updatePopupType(type: PopupTypes) {
        this.popupType = type
        this.requestUpdate()
    }

    private async _getVesselList() {
        const data = await SchedulesService.fetchVesselsList();
        if (data) {
            this.vesselListFetched = true;
        }
        if (data && data.length > 0) {
            const vessels = data.filter((vessel: vesselInfo, index: number, self: Array<vesselInfo>) => {
                return (vessel.primaryVoyagePlanningTool === 3) && 
                (index === self.findIndex((val: vesselInfo) => val?.imo?.toString() === vessel?.imo?.toString()));
              });
            return vessels;
        }
    }

    render() {
        const { title, description } = this.popupContent[this.popupType]

        return this.isOpen
            ? html`
                  <mc-modal
                      ?open=${this.isOpen}
                      dimension="medium"
                      width="525px"
                      height="500px"
                      backdropcloseactiondisabled
                      escapecloseactiondisabled
                      entercloseactiondisabled
                      hiddenclose
                  >
                       ${VoyageImoModel?.imo! ? html `
                        <span slot="heading" class="heading">${title}</span>` : html `
                        <div slot="heading" class="logo-wrapper">
                            <div class="logo">
                                <span class="name">StarConnect</span>
                                <div class="logo-divider"></div>
                                <span class="label app-name">${this.appName}</span>
                                <span class="logo-icon">${SvgIcons.customCloudLogo}</span>
                            </div>
                            <div class="left">
                                <span class="version">${(<any>window)["appenv"].appVersion}</span>
                            </div>
                        </div>`
                       }
                       ${this.vesselListFetched
                            ? this.isUnauthorizedVessel
                                ? html`<p style="text-align:center;padding:24px 16px;">Access denied. You do not have permission to use this application.</p>`
                                : html`<voy-vessel-search .vessels=${this.vessels}></voy-vessel-search>`
                            : html`<div class="spinner"><mc-loading-indicator fit="large" label="Loading vessels"></mc-loading-indicator></div>`}
                      ${VoyageImoModel?.imo! ? html `<mc-button
                          slot="primaryAction"
                          appearance="neutral"
                          @click=${this.closePopup}>
                          Cancel
                      </mc-button>` : html ``
                      }
                  </mc-modal>
              `
            : html``
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'imo-input-popup': ImoInputPopup
    }
}
